<template>
  <v-card elevation="0" rounded="lg" class="pa-3 border">
    <div class="d-flex justify-end mb-1">
      <v-avatar :color="iconColor" size="40" variant="tonal" rounded="lg">
        <v-icon :color="iconColor" size="24">{{ icon }}</v-icon>
      </v-avatar>
    </div>

    <template v-if="showPartialActivation && supportsActivation">
      <div class="pl-2">
        <template v-if="effectiveComparisonActive">
          <div class="text-h4 font-weight-bold mb-2">-</div>
          <div class="text-body-2 text-medium-emphasis mb-2">
            {{ title }}
          </div>
          <v-divider class="mb-5"></v-divider>
          <v-btn color="accent-color" variant="elevated" size="x-small" class="text-none" :to="activationRoute">
            Activate {{ title }}
          </v-btn>
        </template>

        <template v-else>
          <div class="text-h5 font-weight-bold mb-2">-</div>
          <v-btn color="accent-color" variant="elevated" size="x-small" class="text-none" :to="activationRoute">
            Activate {{ title }}
          </v-btn>
        </template>
      </div>

    </template>

    <template v-else>
      <div class="pl-2">
        <div class="text-h4 font-weight-bold mb-1" style="font-size: calc(2.125rem - 2px);">
          <v-progress-circular v-if="metricStatus.loading || !metricData" indeterminate color="primary" size="20"
            width="2" />
          <span v-else>{{ formattedValue }}</span>
        </div>

        <div class="text-body-2 text-medium-emphasis" :class="showComparisonSection ? 'mb-2' : 'mb-0'">
          {{ title }}
        </div>

        <div v-if="showComparisonSection">
          <v-divider class="mb-2"></v-divider>

          <div class="d-flex align-center">
            <v-icon size="16" :color="percentageChange >= 0 ? 'success' : 'error'">
              {{ getChangeIcon(percentageChange) }}
            </v-icon>

            <span class="text-body-2 font-weight-medium ml-1"
              :class="percentageChange >= 0 ? 'text-success' : 'text-error'">
              {{ absPercentageChange }}%
            </span>
          </div>

          <div class="text-caption text-medium-emphasis mt-1">
            {{ comparisonLabel }}
          </div>
        </div>

      </div>
    </template>

  </v-card>
</template>
<script>
const COMPARISON_LABELS = Object.freeze({
  previous_period: 'vs previous period',
  previous_year: 'vs previous year'
})

const DEFAULT_COMPARISON_LABEL = 'vs last period'

const NUMBER_FORMAT = {
  THOUSAND: 1000,
  MILLION: 1000000,
  ONE_DECIMAL: 1
}

export default {
  name: 'MetricCard',

  data() {
    return {
      previousComparisonActive: false
    }
  },

  props: {
    title: {
      type: String,
      required: true
    },
    icon: {
      type: String,
      required: true
    },
    iconColor: {
      type: String,
      required: true
    },
    metricKey: {
      type: String,
      required: true
    },
    metricData: {
      type: Object,
      default: null
    },
    metricStatus: {
      type: Object,
      required: true
    },
    isComparisonActive: {
      type: Boolean,
      required: true
    },
    cardsLoading: {
      type: Boolean,
      default: false
    },
    showPartialActivation: {
      type: Boolean,
      default: false
    },
    supportsActivation: {
      type: Boolean,
      default: false
    },
    activationRoute: {
      type: Object,
      default: null
    },
    valueFormatter: {
      type: Function,
      default: null
    },
    comparisonValue: {
      type: String,
      default: ''
    }
  },

  watch: {
    isComparisonActive: {
      immediate: true,
      handler(newVal) {
        if (!this.cardsLoading) {
          this.previousComparisonActive = newVal
        }
      }
    },
    cardsLoading(newVal) {
      if (!newVal) {
        this.previousComparisonActive = this.isComparisonActive
      }
    }
  },

  computed: {
    effectiveComparisonActive() {
      return this.cardsLoading ? this.previousComparisonActive : this.isComparisonActive
    },

    percentageChange() {
      return this.metricData?.total_percentage_change
    },

    absPercentageChange() {
      const change = this.percentageChange
      if (change === undefined) return 0
      return Math.abs(change)
    },

    showComparisonSection() {
      return this.effectiveComparisonActive && this.percentageChange !== undefined
    },

    formattedValue() {
      const total = this.metricData?.total ?? 0
      if (this.valueFormatter) {
        return this.valueFormatter(total)
      }
      return this.formatNumber(total)
    },

    comparisonLabel() {
      return COMPARISON_LABELS[this.comparisonValue] || DEFAULT_COMPARISON_LABEL
    }
  },

  methods: {
    formatNumber(num) {
      if (num >= NUMBER_FORMAT.MILLION) {
        return (num / NUMBER_FORMAT.MILLION).toFixed(NUMBER_FORMAT.ONE_DECIMAL) + 'M'
      }
      if (num >= NUMBER_FORMAT.THOUSAND) {
        return (num / NUMBER_FORMAT.THOUSAND).toFixed(NUMBER_FORMAT.ONE_DECIMAL) + 'K'
      }
      return String(num)
    },

    getChangeIcon(change) {
      return change >= 0 ? 'mdi-arrow-up' : 'mdi-arrow-down'
    }
  }
}
</script>
